import {
  ABILITY_COUNT,
  Abilities,
  Buff,
  Falling,
  Instability,
  PlayerTag,
  Transform,
} from '@/components'
import type { SystemFn } from '@/core/ecs/Scheduler'
import { Vector3 } from 'three'
import { abilityDef } from '@/game/abilities/definitions'
import { COLLAPSE_START_SECONDS, ArenaResource } from '@/game/arenas/Arena'
import { Events } from '@/game/events'
import {
  EventsResource,
  MatchResource,
  PLAYER_COLORS,
  ViewerResource,
  fighterName,
  teamName,
  type MatchState,
} from '@/game/resources'
import type { Renderer } from '@/gfx/Renderer'
import { InputResource } from '@/input/InputManager'
import type { FighterHudState, Hud, HudState } from '@/ui/Hud'

const MARGIN = 34
/** How long an icon stays lit after a press it could not answer, in seconds. */
const DENIED_FLASH = 0.22

/**
 * Reads the world and fills in the HUD once per frame.
 *
 * The HUD owns no game state of its own — everything it shows is derived here,
 * which means it can never drift out of sync with the simulation.
 */
export function createHudSystem(hud: Hud, renderer: Renderer): SystemFn {
  const projected = new Vector3()

  const emptyFighter = (): FighterHudState => ({
    name: '',
    color: 0xffffff,
    human: false,
    team: 0,
    score: 0,
    instability: 0,
    shield: 0,
    haste: 0,
    alive: true,
    marker: null,
    kind: 'guard',
    abilities: Array.from({ length: ABILITY_COUNT }, () => ({
      charges: 0,
      maxCharges: 1,
      progress: 1,
      denied: false,
    })),
  })

  /** Seconds left on each slot's denial flash, indexed [fighter][ability]. */
  const denied: number[][] = []

  const state: HudState = {
    visible: false,
    timeLeft: 0,
    timerWarning: false,
    winsNeeded: 3,
    fighters: [],
    announceBig: '',
    announceSub: '',
    padConnected: false,
    expanded: true,
    ping: null,
  }

  return ({ world, dt }) => {
    const match = world.resource(MatchResource)
    const arena = world.resource(ArenaResource)
    const input = world.tryResource(InputResource)
    const viewer = world.resource(ViewerResource)

    for (const row of denied) {
      for (let i = 0; i < row.length; i++) row[i] = Math.max(0, row[i] - dt)
    }
    for (const ability of world.resource(EventsResource).read(Events.Ability)) {
      if (ability.phase !== 'fail') continue
      while (denied.length <= ability.player) {
        denied.push(Array.from({ length: ABILITY_COUNT }, () => 0))
      }
      denied[ability.player][ability.ability] = DENIED_FLASH
    }

    state.visible = match.phase !== 'menu' && match.phase !== 'matchOver'
    state.winsNeeded = match.winsNeeded
    state.padConnected = input?.gamepadConnected ?? false
    state.ping = viewer.ping
    state.timeLeft = Math.max(0, match.roundLimit - match.roundTime)
    state.timerWarning =
      match.phase === 'fight' && match.roundTime >= COLLAPSE_START_SECONDS - 3
    // Explanations while nothing is happening; out of the way once it is.
    state.expanded = match.phase !== 'fight' && match.phase !== 'resolving'

    while (state.fighters.length < match.controllers.length) {
      state.fighters.push(emptyFighter())
    }
    state.fighters.length = match.controllers.length

    for (let slot = 0; slot < match.controllers.length; slot++) {
      const fighter = state.fighters[slot]
      fighter.name = fighterName(match, slot)
      // A player's slot the server handed to a bot: their line dropped.
      if (match.names[slot] && match.controllers[slot] === 'ai') fighter.name += ' · НЕТ СВЯЗИ'
      fighter.color = PLAYER_COLORS[slot % PLAYER_COLORS.length]
      // An ability bar for each slot played on this screen: offline both
      // humans at the keyboard and the pad, online only this browser's own.
      fighter.human = viewer.slots.includes(slot)
      // Score belongs to the team. In a free-for-all a fighter is their own
      // team, so this is the same number it always was.
      fighter.score = match.score[match.teams[slot] ?? slot] ?? 0
      fighter.team = match.teams[slot] ?? slot
      fighter.instability = 0
      fighter.shield = 0
      fighter.haste = 0
      fighter.alive = !match.fallen[slot]
      fighter.marker = null
      fighter.kind = match.models[slot] ?? 'guard'
      for (let i = 0; i < ABILITY_COUNT; i++) {
        fighter.abilities[i].charges = 0
        fighter.abilities[i].maxCharges = abilityDef(i).maxCharges
        fighter.abilities[i].progress = 0
        fighter.abilities[i].denied = (denied[slot]?.[i] ?? 0) > 0
      }
    }

    for (const e of world.query([PlayerTag]).entities()) {
      const tag = world.read(e, PlayerTag)
      const fighter = state.fighters[tag.player]
      if (!fighter) continue

      fighter.instability = world.get(e, Instability)?.value ?? 0
      const buff = world.get(e, Buff)
      fighter.shield = buff?.shield ?? 0
      fighter.haste = buff?.haste ?? 0

      const abilities = world.get(e, Abilities)
      if (abilities) {
        for (let i = 0; i < ABILITY_COUNT; i++) {
          const max = abilities.maxCharges[i]
          const charges = abilities.charges[i]
          fighter.abilities[i].charges = charges
          fighter.abilities[i].maxCharges = max
          fighter.abilities[i].progress =
            charges >= max
              ? 1
              : 1 - abilities.recharge[i] / Math.max(abilityDef(i).cooldown, 0.001)
        }
      }

      // Once a fighter is over the edge they leave the frame; an edge marker is
      // the difference between "where did they go" and "they are falling, left".
      if (world.has(e, Falling)) {
        fighter.marker = projectMarker(world.read(e, Transform), renderer, projected)
      }
    }

    const announce = announcementFor(match, arena.warning)
    state.announceBig = announce.big
    state.announceSub = announce.sub

    hud.update(state)
  }
}

function announcementFor(
  match: MatchState,
  collapseWarning: boolean,
): { big: string; sub: string } {
  const score = match.score.join(' : ')
  switch (match.phase) {
    case 'countdown':
      return match.phaseTimer > 0.45
        ? {
            big: String(Math.max(1, Math.ceil(match.phaseTimer - 0.2))),
            sub: 'ПРИГОТОВИТЬСЯ',
          }
        : { big: 'БОЙ!', sub: '' }
    case 'roundOver':
      return match.roundWinner === null
        ? { big: 'НИЧЬЯ', sub: score }
        : {
            big: `РАУНД · ${teamName(match, match.roundWinner)}`,
            sub: score,
          }
    case 'fight':
      return collapseWarning
        ? { big: '', sub: 'ПЛОЩАДКА РУШИТСЯ' }
        : { big: '', sub: '' }
    default:
      return { big: '', sub: '' }
  }
}

/** Projects a world position to the nearest point on the screen border. */
function projectMarker(
  transform: { x: number; y: number; z: number },
  renderer: Renderer,
  scratch: Vector3,
): { x: number; y: number } | null {
  scratch.set(transform.x, Math.max(transform.y, -6), transform.z)
  scratch.project(renderer.camera)

  const inside =
    scratch.x > -1 && scratch.x < 1 && scratch.y > -1 && scratch.y < 1 && scratch.z < 1
  if (inside) return null

  const width = window.innerWidth
  const height = window.innerHeight
  const x = ((scratch.x + 1) / 2) * width
  const y = ((1 - scratch.y) / 2) * height

  return {
    x: Math.max(MARGIN, Math.min(width - MARGIN, x)),
    y: Math.max(MARGIN, Math.min(height - MARGIN, y)),
  }
}
